import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Star, Loader2 } from "lucide-react";
import QstLayout from "./QstLayout";
import { qstPlans, qstCheckout } from "./api";

export default function QstPricingPage() {
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState(null);
  useEffect(() => {
    document.title = "Pricing — Quality Safety Times";
    qstPlans().then((d) => setPlans(d.plans || [])).catch(() => {});
  }, []);

  async function startCheckout(planKey) {
    setBusy(planKey); setNotice(null);
    try {
      const r = await qstCheckout(planKey);
      if (r.checkout_url) { window.location.href = r.checkout_url; return; }
      if (r.free) { window.location.href = r.next || "/qst/library?free_only=1"; return; }
      if (r.contact_sales) { window.location.href = r.next || "/qst/contact?intent=enterprise"; return; }
      if (r.pending_setup) { setNotice(r.message); return; }
      setNotice("Checkout is being finalized. Please contact us to complete your subscription.");
    } catch {
      setNotice("Checkout unavailable — please try again or contact us.");
    } finally { setBusy(null); }
  }

  return (
    <QstLayout>
      <section className="bg-slate-50 py-14 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold">Pricing</div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-black text-[#0B2545]">Start free. Upgrade when the crew needs more.</h1>
          <p className="mt-4 text-lg text-slate-700 max-w-2xl mx-auto">
            Transparent monthly pricing. Cancel any time. Enterprise pricing is quoted individually.
          </p>
          {notice && (
            <div className="mt-4 max-w-2xl mx-auto rounded-md bg-amber-50 border border-amber-200 text-amber-900 px-4 py-2.5 text-sm text-left" data-testid="qst-pricing-notice">
              {notice}
            </div>
          )}
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {plans.map((p) => (
            <div key={p.key}
              className={`rounded-2xl border-2 p-6 flex flex-col ${p.highlight ? "border-[#F97316] bg-orange-50/50 shadow-xl" : "border-slate-200 bg-white"}`}
              data-testid={`qst-plan-${p.key}`}>
              {p.highlight && (
                <div className="inline-flex items-center gap-1 rounded-full bg-[#F97316] text-white px-2 py-0.5 text-[10px] font-black uppercase tracking-widest w-fit mb-2">
                  <Star className="w-3 h-3" /> Most popular
                </div>
              )}
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{p.for}</div>
              <div className="mt-1 text-2xl font-black text-[#0B2545]">{p.name}</div>
              <div className="mt-4">
                {p.price_per_month === 0 ? (
                  <div className="text-5xl font-black text-[#0B2545]">$0</div>
                ) : p.price_per_month === null ? (
                  <div className="text-3xl font-black text-[#0B2545]">Contact us</div>
                ) : (
                  <>
                    <div className="text-5xl font-black text-[#0B2545]">${p.price_per_month}<span className="text-lg font-bold text-slate-500">/mo</span></div>
                    <div className="text-xs text-slate-500 mt-1">or ${p.price_per_year}/yr — save 2 months</div>
                  </>
                )}
              </div>
              <ul className="mt-5 space-y-2 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-800">
                    <CheckCircle2 className="w-4 h-4 text-[#059669] mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button onClick={() => startCheckout(p.key)} disabled={busy === p.key}
                className={`mt-6 inline-flex items-center justify-center gap-2 rounded-md px-4 py-3 font-bold transition ${p.highlight ? "bg-[#F97316] hover:bg-[#EA580C] text-white" : "bg-[#0B2545] hover:bg-[#0F3B7D] text-white"} disabled:opacity-70`}
                data-testid={`qst-plan-cta-${p.key}`}>
                {busy === p.key ? <><Loader2 className="w-4 h-4 animate-spin" /> Working…</> :
                  <>{p.cta.label} <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          ))}
        </div>
      </section>
    </QstLayout>
  );
}
